import {
  type EventSpotCheckTemplate,
  type SpotCheckTemplate,
  type SpotCheckTemplateCategory,
} from '@solinkcorp/event-standard/lib/src/types';
import settings from '@solinkcorp/settings';
import { ScheduledActivityRuleType } from '@solinkcorp/solink-schedule';
import {
  Button,
  Input,
  SectionNav,
  SectionNavItem,
  SectionNavItemLabel,
  SectionNavProvider,
  Step,
  Steps,
  cn,
} from '@solinkcorp/solink-ui';
import { type AiNotification, type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import { type CategorizedEventSpotChecks } from 'components/SpotCheckPanel/hooks/useAiNotifcations';
import { type CategorizedSpotChecks } from 'components/SpotCheckPanel/hooks/useSpotChecks';
import { Archive, ArrowRight, LayoutGrid, ListChecks, Sparkles } from 'lucide-react';
import React from 'react';
import useLocale from 'src/hooks/useLocale';
import { useSelector } from 'stores/store';
import { useFeatureStandard } from 'utils/EventStandardCache/eventStandardCache';
import { type SpotCheckView } from '../SpotCheckMainContent/SpotCheckMainContent';
import SpotCheckCard from '../SpotCheckMainContent/SpotCheckTemplatesView/SpotCheckCard/Aspen/SpotCheckCard';
import { type SpotCheckCategoryKey } from '../SpotCheckMainContent/SpotCheckTemplatesView/SpotCheckTemplatesView';
import scrollStyles from './SpotCheckScroll.module.sass';
import SpotCheckTable from './SpotCheckTable';

export type { SpotCheckCategoryKey };

interface SpotCheckTemplatesViewProps {
  setView: React.Dispatch<React.SetStateAction<SpotCheckView>>;
  categorizedSpotChecks: CategorizedSpotChecks | undefined;
  categorizedEventSpotChecks: CategorizedEventSpotChecks;
  setSpotCheck: React.Dispatch<React.SetStateAction<ScheduledActivityEntity | undefined>>;
  setEventSpotCheck: React.Dispatch<React.SetStateAction<AiNotification | undefined>>;
  selectedCategoryKey: string | null;
  setSelectedCategoryKey: (key: string | null) => void;
  selectedSpotCheckCategoryKey: SpotCheckCategoryKey | null;
  setSelectedSpotCheckCategoryKey: (key: SpotCheckCategoryKey | null) => void;
  spotCheck: ScheduledActivityEntity | undefined;
  eventSpotCheck: AiNotification | undefined;
  spotCheckSearchPrompt: string;
  setSpotCheckSearchPrompt: React.Dispatch<React.SetStateAction<string>>;
  eventSpotCheckSearchPrompt: string;
  setEventSpotCheckSearchPrompt: React.Dispatch<React.SetStateAction<string>>;
  setFormMode: React.Dispatch<React.SetStateAction<'create' | 'edit'>>;
  setIsExistingSpotCheck: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function SpotCheckTemplatesView(props: SpotCheckTemplatesViewProps) {
  const {
    setView,
    categorizedSpotChecks,
    categorizedEventSpotChecks,
    setSpotCheck,
    setEventSpotCheck,
    selectedCategoryKey,
    setSelectedCategoryKey,
    selectedSpotCheckCategoryKey,
    setSelectedSpotCheckCategoryKey,
    spotCheck,
    eventSpotCheck,
    setSpotCheckSearchPrompt,
    setEventSpotCheckSearchPrompt,
    setFormMode,
    setIsExistingSpotCheck,
  } = props;

  const locale = useLocale();
  const featureStandard = useFeatureStandard();
  const language = locale.locale;
  const spotCheckTemplateCategories = featureStandard?.spotCheckTemplateCategories;
  const spotCheckMode = useSelector((state) => state.filter.spotCheckMode);
  const isEventSpotCheck = spotCheckMode === 'event';
  const isScheduledSpotCheckMode = spotCheckMode && ['scheduled', 'event'].includes(spotCheckMode);

  const templates = isEventSpotCheck
    ? (featureStandard?.eventSpotCheckTemplates ?? [])
    : (featureStandard?.spotCheckTemplates ?? []);

  const activeSpotChecks = categorizedSpotChecks?.active ?? [];
  const archivedSpotChecks = categorizedSpotChecks?.archived ?? [];
  const activeEventSpotChecks = categorizedEventSpotChecks?.active ?? [];
  const archivedEventSpotChecks = categorizedEventSpotChecks?.archived ?? [];

  const templatesByCategory = templates.filter(
    (template) =>
      selectedCategoryKey === 'all' ||
      template.metadata?.templateCategoryKey === selectedCategoryKey,
  );

  const updateSelectedCategoryKey = (key: string | null) => {
    setSelectedCategoryKey(key);
    if (key !== null) {
      setSelectedSpotCheckCategoryKey(null);
    }
  };

  const updateSelectedSpotCheckCategoryKey = (key: SpotCheckCategoryKey | null) => {
    setSelectedSpotCheckCategoryKey(key);
    if (key !== null) {
      setSelectedCategoryKey(null);
    }
  };

  const isCardView = !!selectedCategoryKey;
  const spotChecks =
    selectedSpotCheckCategoryKey === 'active' ? activeSpotChecks : archivedSpotChecks;
  const eventSpotChecks =
    selectedSpotCheckCategoryKey === 'active' ? activeEventSpotChecks : archivedEventSpotChecks;

  const templateTitle =
    spotCheckTemplateCategories?.[selectedCategoryKey ?? '']?.i18n[language].name ??
    locale.general.all;

  const sectionTitle = isCardView
    ? templateTitle
    : selectedSpotCheckCategoryKey === 'archived'
      ? locale.investigations.tabs.archived
      : locale.alarms.active;

  const [spotCheckSearch, setSpotCheckSearch] = React.useState('');

  const handleTemplateClick = (template: SpotCheckTemplate | EventSpotCheckTemplate) => {
    const { id, i18n, ...restOfTemplate } = template;
    const scheduledActivitySpotCheck = !isEventSpotCheck
      ? {
          ...restOfTemplate,
          ruleType: ScheduledActivityRuleType.CRON,
          description: i18n[language].description ?? template.description,
          title: i18n[language].title ?? template.title,
          metadata: {
            ...restOfTemplate.metadata,
            aiChatAnswerFormat: '',
            aiChatPrompt: i18n[language].aiChatPrompt ?? template.metadata.aiChatPrompt,
            aiChatAnswerStates:
              template.metadata?.aiChatAnswerStates?.filter(
                (item) => item.isPositiveHit !== false,
              ) || [],
          },
        }
      : undefined;

    const aiNotificationEventSpotCheck = isEventSpotCheck
      ? {
          ...restOfTemplate,
          aiChatAnswerFormat: '',
          aiChatPrompt: i18n[language].aiChatPrompt ?? template.metadata.aiChatPrompt,
          metadata: {
            ...restOfTemplate.metadata,
            aiChatAnswerFormat: '',
            title: i18n[language].title ?? template.title,
            aiChatAnswerStates:
              template.metadata?.aiChatAnswerStates?.filter(
                (item) => item.isPositiveHit !== false,
              ) || [],
          },
        }
      : undefined;
    setSpotCheck(scheduledActivitySpotCheck as unknown as ScheduledActivityEntity);
    setEventSpotCheck(aiNotificationEventSpotCheck as unknown as AiNotification);
    setView('spot-check-form');
    setFormMode('create');
    setIsExistingSpotCheck(false);
  };

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (spotCheckMode === 'event') {
      setEventSpotCheckSearchPrompt(spotCheckSearch);
    } else {
      setSpotCheckSearchPrompt(spotCheckSearch);
    }
    setView('spot-check-form');
    setFormMode('create');
    setIsExistingSpotCheck(false);
  };

  const renderCategoryNav = (categories: Record<string, SpotCheckTemplateCategory> | undefined) => (
    <>
      <p className="tw px-3 pb-1 pt-2 text-foreground-secondary caption">
        {locale.scheduledAIChat.templates}
      </p>
      <SectionNavItem
        isActive={selectedCategoryKey === 'all'}
        tooltip={locale.general.all}
        onClick={() => updateSelectedCategoryKey('all')}
      >
        <LayoutGrid />
        <SectionNavItemLabel>{locale.general.all}</SectionNavItemLabel>
      </SectionNavItem>
      {categories
        ? Object.keys(categories).map((categoryKey) => (
            <SectionNavItem
              key={categoryKey}
              isActive={categoryKey === selectedCategoryKey}
              tooltip={categories[categoryKey]?.i18n?.[language]?.name ?? categoryKey}
              onClick={() => updateSelectedCategoryKey(categoryKey)}
            >
              <LayoutGrid />
              <SectionNavItemLabel>
                {categories[categoryKey]?.i18n?.[language]?.name}
              </SectionNavItemLabel>
            </SectionNavItem>
          ))
        : null}
    </>
  );

  return (
    <SectionNavProvider defaultCollapsed={false} storageKey="solink-spot-check-section-nav-v1">
      <div className="tw flex h-full min-h-0 bg-background-muted">
        <div
          className={cn(
            'tw flex w-60 shrink-0 flex-col overflow-y-auto border-r border-border-muted bg-background-muted',
            scrollStyles.spotCheckAspenScroll,
          )}
        >
          <SectionNav
            className="tw min-h-0 flex-1"
            aria-label={locale.scheduledAIChat.templates}
            data-testid="spot-check-section-nav"
          >
            {renderCategoryNav(spotCheckTemplateCategories)}
            {isScheduledSpotCheckMode ? (
              <>
                <p className="tw px-3 pb-1 pt-4 text-foreground-secondary caption">
                  {locale.scheduledAIChat.spotChecks}
                </p>
                <SectionNavItem
                  isActive={selectedSpotCheckCategoryKey === 'active'}
                  tooltip={locale.alarms.active}
                  onClick={() => updateSelectedSpotCheckCategoryKey('active')}
                >
                  <ListChecks />
                  <SectionNavItemLabel>{locale.alarms.active}</SectionNavItemLabel>
                </SectionNavItem>
                <SectionNavItem
                  isActive={selectedSpotCheckCategoryKey === 'archived'}
                  tooltip={locale.investigations.tabs.archived}
                  onClick={() => updateSelectedSpotCheckCategoryKey('archived')}
                >
                  <Archive />
                  <SectionNavItemLabel>{locale.investigations.tabs.archived}</SectionNavItemLabel>
                </SectionNavItem>
              </>
            ) : null}
          </SectionNav>
        </div>
        <div
          className={cn(
            'tw min-h-0 min-w-0 flex-1 overflow-y-auto bg-background-muted px-6 py-4',
            scrollStyles.spotCheckAspenScroll,
          )}
        >
          <Steps className="tw w-full min-w-0 [&_[data-slot=card-content]]:min-w-0 [&_[data-slot=step]>[data-slot=card]]:border-border-muted [&_[data-slot=step]>[data-slot=card]]:bg-background-surface [&_[data-slot=step]>[data-slot=card]]:shadow-elevation-1">
            <Step
              icon={<Sparkles className="size-4" />}
              title={sectionTitle}
              className="tw min-w-0 max-w-full"
            >
              {isCardView ? (
                <div className="tw flex min-w-0 max-w-full flex-col gap-4">
                  <form className="tw flex gap-2" onSubmit={submitSearch}>
                    <Input
                      data-testid="spot-check-custom-prompt"
                      className="tw min-w-0 flex-1"
                      value={spotCheckSearch}
                      onChange={(event) => setSpotCheckSearch(event.target.value)}
                      placeholder={locale.scheduledAIChat.structuredOutputInput}
                    />
                    <Button type="submit" size="icon" aria-label={locale.general.search}>
                      <ArrowRight className="size-4" />
                    </Button>
                  </form>
                  <div className="tw grid w-full min-w-0 grid-cols-[repeat(auto-fill,minmax(min(100%,17.5rem),1fr))] gap-4">
                    {templatesByCategory.map((template) => (
                      <SpotCheckCard
                        key={`${selectedCategoryKey}-${template.id}`}
                        variant="aspen"
                        fluid
                        title={template.i18n[language].title ?? template.title}
                        onClick={() => handleTemplateClick(template)}
                        templateThumbnail={`${settings.publicUrl}/${template.metadata?.templateThumbnail}`}
                        description={template.i18n[language].description ?? template.description}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <SpotCheckTable
                  setView={setView}
                  setSpotCheck={setSpotCheck}
                  setEventSpotCheck={setEventSpotCheck}
                  spotChecks={spotChecks}
                  eventSpotChecks={eventSpotChecks}
                  selectedSpotCheckCategoryKey={selectedSpotCheckCategoryKey}
                  spotCheck={spotCheck}
                  eventSpotCheck={eventSpotCheck}
                  setFormMode={setFormMode}
                  setIsExistingSpotCheck={setIsExistingSpotCheck}
                />
              )}
            </Step>
          </Steps>
        </div>
      </div>
    </SectionNavProvider>
  );
}
